import * as E from "fp-ts/Either";
import * as O from "fp-ts/Option";
import * as Except from "@discovery/prelude/lib/data/exception";
import * as M from "@discovery/prelude/lib/data/maybe";
import * as V from "@discovery/prelude/lib/data/validated";
import * as Assoc from "../association";
import * as Bundle from "../bundle";
import { never } from "@discovery/prelude/lib/data/utils";
import { showResourceObject, targets2Relationships, } from "../";
import { lookupException } from "./lookup-exception";
import * as RA from "fp-ts/ReadonlyArray";
import { constant, flow, pipe } from "fp-ts/function";
export * from "./optics";
/**
 * NOTICE! Response is as nested `Either` where the `outer left` is based on Client defined exceptions
 * and `inner left` is primarily used as the `sonic error` that is documented here:
 * https://docs.disco-api.com/apidocs/#errors
 */
export const URI = "Response";
export const unknownResourceObjectIdentifierString = (o) => (o.id + o.type);
export const Response = (response) => ({ _tag: "Response", response });
export const toIncluded = (arr) => {
    const map = new Map();
    for (const arri of arr) {
        const id = unknownResourceObjectIdentifierString({
            id: arri.id,
            type: arri.type,
        });
        map.set(id, arri);
    }
    return map;
};
export const response = (i, r, e) => Response([toIncluded(i), r, e]);
const response_ = (i, r, e) => Response([i, r, e]);
/**
 * Maps the `A` that is two levels deep in the nested either
 */
export const map = (g, { response: [i, r, e] }) => response_(i, r, pipe(e, E.map(E.map(g))));
/**
 * Maps the `R`
 */
export const mapR = (g, { response: [i, r, e] }) => response_(i, g(r), e);
/**
 * Allows for extracting the nested value with a default
 */
export const reduce = (g, a, { response: [_, __, e1] }) => pipe(e1, E.reduce(a, (acc, e2) => E.reduce(acc, g)(e2)));
/**
 * Allows for extracting the nested value with a default
 */
export const foldMapConst = (g, a, res) => reduce((_, x) => g(x), a, res);
export const foldArray = (res) => reduce((xs, ys) => RA.getMonoid().concat(xs, ys), RA.empty, res);
export const foldMapArray = (g, res) => foldArray(map(g, res));
export const concatArray = (xs) => pipe(xs, RA.chain((r) => foldMapArray(RA.of, r)));
export const responseOf = (i, r, a) => response(i, r, E.right(E.right(a)));
const responseOf_ = (i, r, a) => response_(i, r, E.right(E.right(a)));
export const sequenceArray = ({ response: [i, r, e], }) => 
// TODO refactor when traverse is available
pipe(e, E.fold((err) => RA.of(response_(i, r, E.left(err))), E.fold((err) => RA.of(response_(i, r, E.right(E.left(err)))), RA.map((x) => responseOf_(i, r, x)))));
export const fromResponse = ({ response: [_, __, e1] }, a) => pipe(e1, E.getOrElse(constant(E.right(a))), E.getOrElse(constant(a)));
/**
 * Allows for recovering based on the inner error
 */
export const recover = (g, { response: [i, r, e1] }) => response_(i, r, pipe(e1, E.map((e2) => pipe(e2, E.orElse(flow(g, E.right))))));
export const foldError = (g, b, { response: [_, __, e] }) => pipe(e, E.fold(constant(b), E.fold((x) => g(b, x), constant(b))));
/**
 * Allows for recovering based on the outer exception
 */
export const recoverResponseException = (g, { response: [i, r, e] }) => response_(i, r, pipe(e, E.fold((x) => E.right(E.right(g(x))), E.right)));
export const foldResponseException = (g, b, { response: [_, __, e] }) => pipe(e, E.fold((x) => g(b, x), () => b));
/**
 * Allows for extracting all the outer exceptions in the either
 */
export const responseExceptionStack = ({ response: [_, __, e], }) => E.fold(Except.exceptionMessageStack, () => [])(e);
/**
 * Allows for recovery either on the `ResponseException` or the validated `E`.
 * Similar to `reduce` but works on the left case of both eithers
 * Our retry logic is based on this.
 *
 * Example: Retries a request based the errorHandler argument
 *
 * ```typescript
 * const retryEndpoint = <R, E, A>(
 *   n: number,
 *   errorHandler: (defaultReq: Endpoint<R, E, A>) => (e: E) => Endpoint<R, E, A>,
 *   endpoint: Endpoint<R, E, A>
 * ): Endpoint<R, E, A> => {
 *   if (n === 1) {
 *     return endpoint;
 *   } else {
 *     return App.chainMerge(res => {
 *       const nextRetry = retryEndpoint(n - 1, errorHandler, endpoint);
 *       return foldExceptionError(
 *         constant(nextRetry),
 *         errorHandler(nextRetry),
 *         App.of,
 *         res
 *       );
 *     }, endpoint);
 *   }
 * };
 * ```
 */
export const foldExceptionError = (f, g, h, { response: [include, relationship, e1] }) => pipe(e1, E.fold(f, E.fold(g, (a) => h(responseOf_(include, relationship, a)))));
// Take out the first resource object where type and id match
export const includedLookup = (included, a) => {
    const id = unknownResourceObjectIdentifierString(a);
    return M.of(included.get(id));
};
/* Cache
 * Cache for validatedLookup has been tested and discarded,
 * it would minimize the resourceObjects lookup and validation when you have multiple different
 * resouceObjects lookup up the same resouceObjects, but this has no effect,
 * probably because the bottleneck is not in the lookup right now, but if it becomes that
 * this should help.
 * Also if one could come up with a smarter cache invalidation strategy then just using `included`
 * then we could get 3x here, but you don't know then you lookup `id + type` -> `ResouceObject`
 * if there is any more fields in `ResouceObject` now due to a new request with more queryparams for example
 */
const validatedLookup = (t, included, id) => M.fromMaybe(
// Validate the received resource object
M.map((x) => V.validate(t, x), includedLookup(included, id)), E.left(lookupException(`Lookup for ${showResourceObject(id)} failed.`)));
const resourceObjectIdentifierLookup = (a, k) => {
    return M.fromMaybe(M.chain((x) => M.map((y) => E.of(y.data), x), M.chain((x) => M.of(x[k]), M.isMaybe(a.relationships) ? a.relationships : M.of(a.relationships))), E.left(lookupException(`Resource ${showResourceObject(a)} is missing relationship "${k}".`)));
};
const includeRelationship = (t, included, id) => {
    return Array.isArray(id)
        ? pipe(id, RA.reduce(E.of(RA.empty), (e, id_) => pipe(e, E.chain((xs) => pipe(validatedLookup(t, included, id_), E.map((a) => RA.append(a)(xs)))))))
        : validatedLookup(t, included, id);
};
const asExceptional = (m, rel) => M.isMaybe(m)
    ? M.fromMaybe(M.map((a) => E.of(a), m), E.left(lookupException(`Could not lookup relationship "${rel}" because the resource object in Response was Nothing.`)))
    : E.of(m);
export const hasRel = (rel, { response: [_, __, e1] }) => pipe(e1, E.map(E.map((m) => pipe(M.isMaybe(m) ? m : O.fromNullable(m), O.map((x) => E.isRight(resourceObjectIdentifierLookup(x, rel))), O.getOrElse(constant(false))))), E.getOrElseW(() => E.right(false)), E.getOrElseW(() => false));
/**
 * Used whenever the relationship is a sumtype and not a single type
 *
 * Example: Relationship `primaryContent` targets `taxonomyNode | video | article ...` but we want `taxonomyNode`
 *
 * ```typescript
 * lookupRelAssoc("primaryContent", pageResponse)("taxonomyNode")
 * ```
 */
/* Two level cache is used to fetch unique cache per cache
 *
 * Weakmap is used because it's weak in the keys so when there is no reference to the key (included)
 * the row gets garbage collected.
 *
 * ResouceObject -> Relationship
 * The relationshipLookupCache is for the case where all videos lookup their channel and that channel looks up images.
 * So from a specific ResourceObject (ex Channel TLC) we want to find "images"
 * if we have done this before we just give the result back imidiatly.
 *
 */
const includedCache = new WeakMap();
export const lookupRelAssoc = (rel, { response: [i, r, e1] }) => (assoc) => {
    const relationshipLookupCache = includedCache.get(i) || new Map();
    const x = r[rel];
    const newR = Bundle.fold((s) => s.single, (m) => m.multiple[assoc])(x);
    const getResourceObjectIdentifier = (a) => resourceObjectIdentifierLookup(a, rel);
    const include = (ids) => {
        return includeRelationship(Assoc.prValidated(newR), i, ids);
    };
    const g = (a) => {
        // USE this parentRelationshipId makes it so if you want to find images for a show
        // and you have previously looked up that relationship then the cache will be used
        // and quite a lot of work gets skipped
        const assocKey = assoc !== null && assoc !== void 0 ? assoc : "";
        const id = a.id + a.type + rel + assocKey;
        if (relationshipLookupCache.has(id)) {
            return relationshipLookupCache.get(id);
        }
        else {
            const ans = E.chain(include)(getResourceObjectIdentifier(a));
            relationshipLookupCache.set(id, ans);
            return ans;
        }
    };
    const newE = pipe(e1, E.chain((e2) => pipe(e2, E.map((m) => E.chain(g)(asExceptional(m, rel))), E.sequence(E.Applicative))));
    const res = response_(i, Assoc.prRelationships(newR), newE);
    includedCache.set(i, relationshipLookupCache);
    return res;
};
/**
 * Used whenever the relationship is mapped to a single type
 *
 * Example: Relationship `routes` targets `routes`
 *
 * ```typescript
 * lookupRel("routes", pageResponse)
 * ```
 */
export const lookupRel = (rel, res) => lookupRelAssoc(rel, res)(never);
/**
 * Takes an object of targets, creates new relationships and adds it to a
 * response relationships.
 *
 * Mainly used for the sonic `oneToOneRelationship`
 * where components can adhoc add relationships that does not necessarily exist
 * in the sonic-api-ng
 *
 * Example:
 *
 * ```typescript
 * const oneToOneRelationships = { cmpLink: Link.Resource.target }
 * const newResponse = addNewRelationships(oneToOneRelationships)(oldResponse);
 * const _CmpLink = Fold.compose(Getter.of<typeof newResponse>(), Res._Rel("cmpLink"));
 * ```
 *
 * @param targets An object of targets with a namspaced `key` where `value`
 * constitutes of the data type you want to point to. These will be the new
 * relationships
 *
 * @param response The response that you want to add the new relationships to
 */
export const addNewRelationships = (targets) => (response) => mapR((rel) => (Object.assign(Object.assign({}, rel), targets2Relationships(targets))), response);

